import clsx from "clsx";
import {
	useCallback,
	useEffect,
	useId,
	useState,
	useRef,
	type CSSProperties,
	type KeyboardEvent,
} from "react";
import { PHASE, STARTING_NUM_TIMEOUTS } from "../../../../common/constants.ts";
import { helpers } from "../util/helpers.ts";
import { formatSeasonRecord } from "../util/seasonRecord.ts";
import { createMobileCrossTabHref } from "../util/mobileCrossTabLink.ts";
import { toWorker } from "../util/toWorker.ts";
import { realtimeUpdate } from "../util/realtimeUpdate.ts";
import { BoxScore } from "./BoxScore.tsx";
import { range } from "../../../../common/utils.ts";
import getWinner from "../../../../common/getWinner.ts";
import {
	translateTeamRegion,
	translateTeamName,
	translateTeamShortName,
} from "../util/teamNames.ts";
import { ResponsivePopover } from "./ResponsivePopover.tsx";
import { TeamLogoInline } from "./TeamLogoInline.tsx";
import { gradientStyleFactory } from "../util/gradientStyleFactory.ts";
import { useLocalPartial } from "../util/local.ts";
import i18n from "../util/i18n.ts";
import { MobileTooltip } from "./MobileTooltip.tsx";
import { TeamAbbrevText } from "./TeamAbbrevLink.tsx";
import {
	expandOvertimeAbbreviation,
	formatOvertimePeriodLabel,
} from "../util/basketballLabels.ts";

const colTitle = (key: string, defaultValue: string) =>
	i18n.t(`title.${key}`, { ns: "cols", defaultValue });

const TeamNameLink = ({
	children,
	className,
	season,
	style,
	t,
}: {
	children: any;
	className?: string;
	season: number;
	style?: CSSProperties;
	t: {
		abbrev: string;
		name: string;
		region: string;
		tid: number;
	};
}) => {
	return (
		<>
			{t.tid >= 0 ? (
				<a
					href={createMobileCrossTabHref(
						helpers.leagueUrl(["roster", `${t.abbrev}_${t.tid}`, season]),
					)}
					className={className}
					style={style}
				>
					{children}
				</a>
			) : (
				<>{children}</>
			)}
		</>
	);
};
const TeamLogo = ({
	season,
	t,
}: {
	season: number;
	t: {
		abbrev: string;
		imgURL?: string;
		name: string;
		region: string;
		tid: number;
		won: number;
		lost: number;
		tied?: number;
		otl?: number;
		timeouts?: number;
	};
}) => {
	return t.imgURL !== undefined && t.imgURL !== "" ? (
		<div className="w-100 d-none d-lg-flex justify-content-center">
			<div>
				<TeamNameLink
					season={season}
					t={t}
					className="d-flex align-items-center justify-content-center"
					style={{ height: 100, width: 120 }}
				>
					<img className="mw-100 mh-100" src={t.imgURL} alt="" />
				</TeamNameLink>
				<div className="mt-1 mb-3 fw-bold text-center">
					{formatSeasonRecord(t)}
				</div>
			</div>
		</div>
	) : null;
};

const TeamNameAndScore = ({
	boxScore,
	live,
	possessionNum,
	shootout,
	t,
}: {
	boxScore: any;
	live: boolean | undefined;
	possessionNum: 0 | 1;
	shootout: boolean;
	t: any;
}) => {
	const LOGO_SIZE = 24;

	return (
		<div className="d-flex">
			{live && !boxScore.gameOver ? (
				<div
					className={
						boxScore.possession === possessionNum ? "text-warning" : "opacity-0"
					}
					style={{ marginTop: -1 }}
				>
					●&nbsp;
				</div>
			) : null}
			{t.playoffs ? (
				<div
					className="text-body-secondary fs-5 align-self-end"
					style={{ paddingBottom: STARTING_NUM_TIMEOUTS !== undefined ? 8 : 1 }}
				>
					{t.playoffs.seed}.&nbsp;
				</div>
			) : null}
			<div>
				<TeamNameLink
					className="d-flex align-items-center gap-1"
					season={boxScore.season}
					t={t}
				>
					<TeamLogoInline
						imgURL={t.imgURL}
						imgURLSmall={t.imgURLSmall}
						size={LOGO_SIZE}
					/>
					<div>
						{t.season !== undefined ? `${t.season} ` : null}
						<span className="d-none d-xl-inline">
							{translateTeamRegion(t.region)} {translateTeamName(t.name)}
						</span>
						<span className="d-none d-lg-inline d-xl-none">
							{translateTeamRegion(t.region)} {translateTeamName(t.name)}
						</span>
						<span className="d-none d-sm-inline d-lg-none">
							{translateTeamName(t.name)}
						</span>
						<span className="d-inline d-sm-none">
							{translateTeamShortName(t, t.abbrev)}
						</span>
					</div>
				</TeamNameLink>
				{t.timeouts !== undefined && STARTING_NUM_TIMEOUTS !== undefined ? (
					<MobileTooltip
						title={i18n.t("boxScoreWrapper.timeoutsRemaining", {
							ns: "ui",
							count: t.timeouts,
							defaultValue: "{{count}} timeouts remaining",
						})}
					>
						<div
							className="d-flex gap-1 pt-1"
							style={{ marginLeft: LOGO_SIZE + 4 }}
						>
							{range(STARTING_NUM_TIMEOUTS).map((i) => (
								<div
									key={i}
									style={{ width: 12, height: 3 }}
									className={i < t.timeouts ? "bg-warning" : "bg-tertiary"}
								/>
							))}
						</div>
					</MobileTooltip>
				) : null}
			</div>
			<div>&nbsp;{t.pts}</div>
			{shootout ? (
				<div className="text-body-secondary">&nbsp;({t.sPts})</div>
			) : null}
		</div>
	);
};

const ScoreboardTeam = ({
	boxScore,
	t,
}: {
	boxScore: any;
	t: any;
}) => {
	const record =
		t.won !== undefined && t.lost !== undefined ? formatSeasonRecord(t) : "";

	return (
		<div className="box-score-game-summary-team">
			<TeamNameLink
				className="box-score-game-summary-logo"
				season={boxScore.season}
				t={t}
			>
				<TeamLogoInline
					imgURL={t.imgURL}
					imgURLSmall={t.imgURLSmall}
					includePlaceholderIfNoLogo
					size={56}
				/>
			</TeamNameLink>
			<div className="box-score-game-summary-team-name">
				<TeamNameLink season={boxScore.season} t={t}>
					{t.season !== undefined ? `${t.season} ` : null}
					<span className="d-none d-sm-inline">
						{translateTeamRegion(t.region)} {translateTeamName(t.name)}
					</span>
					<span className="d-inline d-sm-none">
						{translateTeamShortName(t, t.abbrev)}
					</span>
					{t.playoffs ? (
						<span className="text-body-secondary"> ({t.playoffs.seed})</span>
					) : null}
				</TeamNameLink>
			</div>
			{record ? (
				<div className="box-score-game-summary-record text-body-secondary">
					{record}
				</div>
			) : null}
		</div>
	);
};

export const HeadlineScoreLive = ({
	boxScore,
	isStuck,
}: {
	boxScore: any;
	isStuck: boolean;
}) => {
	// Historical games will have boxScore.won.name and boxScore.lost.name so use that for ordering, but live games
	// won't. This is hacky, because the existence of this property is just a historical coincidence, and maybe it'll
	// change in the future.
	const t0 =
		boxScore.won?.name !== undefined ? boxScore.won : boxScore.teams[0];
	const t1 =
		boxScore.lost?.name !== undefined ? boxScore.lost : boxScore.teams[1];

	const shootout = t0.sPts !== undefined;

	const clockText = boxScore.gameOver
		? i18n.t("boxScoreWrapper.finalStatus", {
				ns: "ui",
				defaultValue: "Final",
			})
		: boxScore.elamTarget !== undefined
			? i18n.t("liveGame.elamTargetDisplay", {
					ns: "ui",
					target: boxScore.elamTarget,
					defaultValue: "Target: {{target}} points",
				})
			: `${expandOvertimeAbbreviation(boxScore.quarterShort)}, ${boxScore.time}`;

	return (
		<div className="d-flex justify-content-center">
			<div
				className={`d-flex flex-wrap align-items-center ${isStuck ? "live-game-score-actual-stuck" : "bg-white"} py-md-1 px-md-2 rounded-bottom-4 live-game-score-actual`}
			>
				<div className="d-flex h2 mb-0">
					<TeamNameAndScore
						boxScore={boxScore}
						possessionNum={0}
						live
						shootout={shootout}
						t={t0}
					/>
				</div>
				<div className="d-flex h2 mb-0 ms-3">
					<TeamNameAndScore
						boxScore={boxScore}
						possessionNum={1}
						live
						shootout={shootout}
						t={t1}
					/>
					{boxScore.overtime ? <div>&nbsp;{boxScore.overtime}</div> : null}
				</div>
				<div className="d-flex h2 mb-0 ms-auto ms-md-3">
					<div
						className="text-nowrap fs-6 align-self-end"
						style={{
							paddingBottom: 2,
							paddingTop: STARTING_NUM_TIMEOUTS !== undefined ? 0 : 7,
						}}
					>
						{clockText}
					</div>
				</div>
			</div>
		</div>
	);
};

const HeadlineScore = ({ boxScore }: { boxScore: any }) => {
	// Historical games will have boxScore.won.name and boxScore.lost.name so use that for ordering, but live games
	// won't. This is hacky, because the existence of this property is just a historical coincidence, and maybe it'll
	// change in the future.
	const t0 =
		boxScore.won?.name !== undefined ? boxScore.won : boxScore.teams[0];
	const t1 =
		boxScore.lost?.name !== undefined ? boxScore.lost : boxScore.teams[1];

	const shootout = t0.sPts !== undefined;
	const gameInfoText =
		boxScore.day !== undefined
			? i18n.t("boxScoreWrapper.gameDay", {
					ns: "ui",
					defaultValue: "{{season}} season, day {{day}}",
					season: boxScore.season,
					day: boxScore.day,
				})
			: i18n.t("boxScoreWrapper.seasonOnly", {
					ns: "ui",
					defaultValue: "{{season}} season",
					season: boxScore.season,
				});
	const score0 = shootout ? `${t0.pts} (${t0.sPts})` : t0.pts;
	const score1 = shootout ? `${t1.pts} (${t1.sPts})` : t1.pts;

	return (
		<div
			className="box-score-game-summary"
		>
			<ScoreboardTeam boxScore={boxScore} t={t0} />
			<div className="box-score-game-summary-center">
				<div className="box-score-game-summary-meta">{gameInfoText}</div>
				<h2 className="box-score-game-summary-score">
					<span>{score0}</span>
					<span className="box-score-game-summary-separator">-</span>
					<span className="box-score-game-summary-loser">{score1}</span>
				</h2>
				<div className="box-score-game-summary-status">
					{i18n.t("boxScoreWrapper.finalStatus", {
						ns: "ui",
						defaultValue: "Final",
					})}
					{boxScore.overtime ? ` ${boxScore.overtime}` : null}
				</div>
			</div>
			<ScoreboardTeam boxScore={boxScore} t={t1} />
		</div>
	);
};

/**
 * https://mail.google.com/mail/u/0/#inbox/FMfcgzQXJkPnPxpcVRXBlWLkzstDqRhL?compose=DmwnWsCZDhLblTHmqrNNxtXpHpcPFDdgTvQWnRHjZxklJKgDrKvMXQjlFhvrMzWLxNgRsnbTGzqv
 * Shooting factor: FG Pts - FGM*LgEffic - (1-LgOR%)*FGX*LgEffic
 * Turnover factor: -LgEffic*TOV
 * Rebound factor: [(1-LgOR%)*OREB - LgOR%*OppDREB]*LgEffic
 * Free throw factor: FTM - 0.4*FTA*LgEffic + 0.06*(FTA-FTM)*LgEffic
 * LgEffic is the league average points per possession (like 1.06, for example). In the NBA, it's about 1.15. In the Olympics, it was about 1.11. In the WNBA it's around 1.02. Lower at lower levels, usually.
 * LgOR% is the league average offensive rebounding percentage. It tends to be about 0.28, though can be higher at lower levels.
 * Do those calculations for each team, then do the difference so things approximately add up to the final score difference.
 */
const getFourFactorsNetPoints = (teams: any[]) => {
	// These should be adjusted for league averages, but doens't seem to matter much, so whatever
	const lgEffic = 1.15;
	const lgOrbPct = 0.28;

	// For testing with data from https://x.com/DeanO_Lytics/status/1846229178808406137
	/*const teams = [
		{
			fg: 43,
			fga: 92,
			tp: 16,
			tov: 16,
			ft: 18,
			fta: 28,
			drb: 30,
			orb: 17,
		},
		{
			fg: 43,
			fga: 79,
			tp: 13,
			tov: 18,
			ft: 17,
			fta: 29,
			drb: 33,
			orb: 7,
		},
	];*/

	const fourFactorsNetPoints = {
		efg: 0,
		tov: 0,
		orb: 0,
		ft: 0,
	};
	for (let i = 0; i < teams.length; i++) {
		const t = teams[i];
		const t2 = teams[1 - i];

		const sign = i === 0 ? 1 : -1;

		const fgPts = 2 * t.fg + t.tp;

		fourFactorsNetPoints.efg +=
			sign *
			(fgPts - t.fg * lgEffic - (1 - lgOrbPct) * (t.fga - t.fg) * lgEffic);
		fourFactorsNetPoints.tov += sign * (-lgEffic * t.tov);
		fourFactorsNetPoints.orb +=
			sign * ((1 - lgOrbPct) * t.orb - lgOrbPct * t2.drb) * lgEffic;
		fourFactorsNetPoints.ft +=
			sign * (t.ft - 0.4 * t.fta * lgEffic + 0.06 * (t.fta - t.ft) * lgEffic);
	}

	return fourFactorsNetPoints;
};

const FourFactorsAmountLine = ({
	amount,
	teams,
}: {
	amount: number;
	teams: any[];
}) => {
	const team = amount > 0 ? teams[0] : teams[1];
	const amountFixed = Math.abs(amount).toFixed(1);
	if (amountFixed === "0.0") {
		return i18n.t("boxScoreWrapper.even", { ns: "ui", defaultValue: "even" });
	}

	return i18n.t("boxScoreWrapper.pointsForTeam", {
		ns: "ui",
		defaultValue: "+{{amount}} for {{abbrev}}",
		amount: amountFixed,
		abbrev: translateTeamShortName(team, team.abbrev),
	});
};

const FourFactorsTotalLine = ({
	fourFactorsNetPoints,
	teams,
}: {
	fourFactorsNetPoints: ReturnType<typeof getFourFactorsNetPoints>;
	teams: any[];
}) => {
	const total =
		fourFactorsNetPoints.efg +
		fourFactorsNetPoints.tov +
		fourFactorsNetPoints.orb +
		fourFactorsNetPoints.ft;
	const totalFixed = Math.abs(total).toFixed(1);
	if (totalFixed === "0.0") {
		return i18n.t("boxScoreWrapper.even", { ns: "ui", defaultValue: "even" });
	}

	const team = total > 0 ? teams[0] : teams[1];
	return i18n.t("boxScoreWrapper.pointsForTeam", {
		ns: "ui",
		defaultValue: "+{{amount}} for {{abbrev}}",
		amount: totalFixed,
		abbrev: translateTeamShortName(team, team.abbrev),
	});
};

const FourFactors = ({ teams }: { teams: any[] }) => {
	const fourFactorsNetPoints = getFourFactorsNetPoints(teams);
	const fourFactorCols = [
		{
			stat: "stat_efg",
			defaultTitle: "eFG%",
			description: i18n.t("boxScoreWrapper.effectiveFieldGoalPercentage", {
				ns: "ui",
				defaultValue: "Advanced metric: Effective Field Goal Percentage",
			}),
			amount: fourFactorsNetPoints.efg,
		},
		{
			stat: "stat_tovp",
			defaultTitle: "TOV%",
			description: i18n.t("boxScoreWrapper.turnoverPercentage", {
				ns: "ui",
				defaultValue: "Advanced metric: Turnover Percentage",
			}),
			amount: fourFactorsNetPoints.tov,
		},
		{
			stat: "stat_orbp",
			defaultTitle: "ORB%",
			description: i18n.t("boxScoreWrapper.offensiveReboundPercentage", {
				ns: "ui",
				defaultValue: "Advanced metric: Offensive Rebound Percentage",
			}),
			amount: fourFactorsNetPoints.orb,
		},
		{
			stat: "stat_ftpFga",
			defaultTitle: "FT/FGA",
			description: i18n.t(
				"boxScoreWrapper.freeThrowsMadeOverFieldGoalAttempts",
				{
					ns: "ui",
					defaultValue:
						"Advanced metric: Free Throws Made Over Field Goal Attempts",
				},
			),
			amount: fourFactorsNetPoints.ft,
		},
	];

	// +10 shows up in the gradient as maximum color, unless there is one component beyond that, in which case we use that value to highlight how extreme it is. But keeping +10 as the minumum possible max is good to show when they are all pretty close.
	const maxMagnitude = Math.max(
		Math.max(...Object.values(fourFactorsNetPoints).map((x) => Math.abs(x))),
		10,
	);

	const gradientStyle = gradientStyleFactory(-maxMagnitude, 0, 0, maxMagnitude);

	const detailsTitle = i18n.t("boxScoreWrapper.fourFactorsDetails", {
		ns: "ui",
		defaultValue: "Advanced metric details",
	});

	const detailsBody = (
		<>
			<p>
				{i18n.t("boxScoreWrapper.fourFactorsNetPoints", {
					ns: "ui",
					defaultValue: "Estimated value of key stats in net points:",
				})}
			</p>
			<ul className="list-unstyled mb-0">
				{fourFactorCols.map((col) => (
					<li key={col.stat}>
						{colTitle(col.stat, col.defaultTitle)}:{" "}
						<FourFactorsAmountLine amount={col.amount} teams={teams} />
						<div className="small text-body-secondary">{col.description}</div>
					</li>
				))}
				<li className="fw-bold">
					{i18n.t("common.total", { ns: "ui", defaultValue: "Total" })}
					{": "}
					<FourFactorsTotalLine
						fourFactorsNetPoints={fourFactorsNetPoints}
						teams={teams}
					/>
				</li>
			</ul>
		</>
	);

	// B4: ResponsivePopover opens the four-factors detail as a modal on mobile
	// (the click popover stays on desktop).
	return (
		<ResponsivePopover
			id="box-score-four-factors"
			modalHeader={detailsTitle}
			modalBody={detailsBody}
			placement="bottom"
			popoverContent={
				<div style={{ width: 250, maxWidth: "calc(100vw - 32px)" }}>
					{detailsBody}
				</div>
			}
			renderTarget={({ onClick }) => (
			<table
				aria-label={detailsTitle}
				className="table table-sm mb-0 cursor-pointer box-score-four-factors-table"
				data-box-score-four-factors-table=""
				onClick={onClick}
			>
				<thead>
					<tr>
						<th scope="col">
							{i18n.t("boxScore.team", {
								ns: "ui",
								defaultValue: "Team",
							})}
						</th>
						{fourFactorCols.map((col) => (
							<th key={col.stat} scope="col">
								{colTitle(col.stat, col.defaultTitle)}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{teams.map((t, i) => {
						const t2 = teams[1 - i];

						const efg = (100 * (t.fg + t.tp / 2)) / t.fga;
						const tovp = (100 * t.tov) / (t.fga + 0.44 * t.fta + t.tov);
						const orbp = (100 * t.orb) / (t.orb + t2.drb);
						const ftpFga = t.ft / t.fga;

						const efg2 = (100 * (t2.fg + t2.tp / 2)) / t2.fga;
						const tovp2 = (100 * t2.tov) / (t2.fga + 0.44 * t2.fta + t2.tov);
						const orbp2 = (100 * t2.orb) / (t2.orb + t.drb);
						const ftpFga2 = t2.ft / t2.fga;

						// Can't always take Math.abs because sometimes the 4 factors leader is not the net pts leader, since the 4 factors values are not used directly in the net pts formulas
						const gradientSign = i === 0 ? 1 : -1;

						return (
							<tr data-box-score-four-factors-team={t.tid} key={t.abbrev}>
								<th scope="row">{translateTeamShortName(t, t.abbrev)}</th>
								<td
									style={
										efg > efg2
											? gradientStyle(gradientSign * fourFactorsNetPoints.efg)
											: undefined
									}
								>
									{helpers.roundStat(efg, "efg")}
								</td>
								<td
									style={
										tovp < tovp2
											? gradientStyle(gradientSign * fourFactorsNetPoints.tov)
											: undefined
									}
								>
									{helpers.roundStat(tovp, "tovp")}
								</td>
								<td
									style={
										orbp > orbp2
											? gradientStyle(gradientSign * fourFactorsNetPoints.orb)
											: undefined
									}
								>
									{helpers.roundStat(orbp, "orbp")}
								</td>
								<td
									style={
										ftpFga > ftpFga2
											? gradientStyle(gradientSign * fourFactorsNetPoints.ft)
											: undefined
									}
								>
									{helpers.roundStat(ftpFga, "ftpFga")}
								</td>
							</tr>
						);
					})}
				</tbody>
			</table>
			)}
		/>
	);
};

const NextButton = ({
	abbrev,
	boxScore,
	currentGidInList,
	nextGid,
	tid,
}: {
	abbrev?: string;
	boxScore: any;
	currentGidInList?: boolean;
	nextGid?: number;
	tid?: number;
}) => {
	const [autoGoToNext, setAutoGoToNext] = useState(false);
	const [clickedGoToNext, setClickedGoToNext] = useState(false);

	const simNext = useCallback(async () => {
		setAutoGoToNext(true);
		setClickedGoToNext(true);
		await toWorker("playMenu", "day", undefined);
	}, []);

	useEffect(() => {
		let mounted = true;
		const whatever = async () => {
			if (autoGoToNext && nextGid !== undefined) {
				setAutoGoToNext(false);
				// Hack, because otherwise the updateEvent with "gameSim" comes before this one, but doesn't finish yet, so in updatePage this update gets canceled even though it's a new URL (because it's the same page)
				setTimeout(() => {
					realtimeUpdate(
						[],
						helpers.leagueUrl([
							"game_log",
							`${abbrev}_${tid}`,
							boxScore.season,
							nextGid,
						]),
					);

					if (mounted) {
						setClickedGoToNext(false);
					}
				}, 10);
			}
		};

		whatever();

		return () => {
			mounted = false;
		};
	}, [abbrev, autoGoToNext, boxScore.season, nextGid, tid]);

	const { phase, playMenuOptions, season } = useLocalPartial([
		"phase",
		"playMenuOptions",
		"season",
	]);

	const canPlay = playMenuOptions.some(
		(option) => option.id === "day" || option.id === "week",
	);

	return (
		<>
			{boxScore.season === season &&
			currentGidInList &&
			(nextGid === undefined || clickedGoToNext || autoGoToNext) &&
			phase >= PHASE.REGULAR_SEASON &&
			phase <= PHASE.PLAYOFFS ? (
				<button
					className="btn btn-light-bordered box-score-game-nav-btn"
					disabled={!canPlay}
					onClick={simNext}
				>
					{i18n.t("boxScoreWrapper.simNextGame", {
						ns: "ui",
						defaultValue: "Sim next game",
					})}
				</button>
			) : (
				<a
					className={clsx("btn", "btn-light-bordered", "box-score-game-nav-btn", {
						disabled: nextGid === undefined,
					})}
					href={helpers.leagueUrl([
						"game_log",
						abbrev === "special" ? abbrev : `${abbrev}_${tid}`,
						boxScore.season,
						nextGid,
					])}
				>
					{i18n.t("boxScoreWrapper.nextGame", {
						ns: "ui",
						defaultValue: "Next game",
					})}
				</a>
			)}
		</>
	);
};

const DetailedScore = ({
	abbrev,
	boxScore,
	currentGidInList,
	nextGid,
	prevGid,
	showNextPrev,
	tid,
}: {
	abbrev?: string;
	boxScore: any;
	currentGidInList?: boolean;
	nextGid?: number;
	prevGid?: number;
	showNextPrev?: boolean;
	tid?: number;
}) => {
	// Quarter/overtime labels
	const numPeriods = Math.max(
		boxScore.teams[0].ptsQtrs.length,
		boxScore.numPeriods ?? 0,
	);
	const qtrs: {
		title?: string;
		label: string;
		bold?: boolean;
	}[] = range(numPeriods).map((i) => {
		return {
			label:
				i < boxScore.numPeriods
					? `${i + 1}`
					: formatOvertimePeriodLabel(i - boxScore.numPeriods + 1),
		};
	});

	qtrs.push({
		label: i18n.t("boxScoreWrapper.finalScoreShort", {
			ns: "ui",
			defaultValue: "F",
		}),
		title: i18n.t("boxScoreWrapper.finalScore", {
			ns: "ui",
			defaultValue: "Final score",
		}),
		bold: true,
	});

	const shootout = boxScore.teams[0].sPts !== undefined;

	if (shootout) {
		qtrs.push({
			label: "S",
			title: i18n.t("boxScoreWrapper.shootout", {
				ns: "ui",
				defaultValue: "Shootout",
			}),
			bold: true,
		});
	}

	const [activeTab, setActiveTab] = useState<"lineScore" | "fourFactors">(
		"lineScore",
	);
	const detailTabsId = useId().replaceAll(":", "");
	const detailTabRefs = useRef<Array<HTMLButtonElement | null>>([]);
	const detailTabs = [
		{
			id: "lineScore",
			label: i18n.t("boxScoreWrapper.lineScore", {
				ns: "ui",
				defaultValue: "Scoring by period",
			}),
		},
		{
			id: "fourFactors",
			label: i18n.t("boxScoreWrapper.fourFactors", {
				ns: "ui",
				defaultValue: "Advanced",
			}),
		},
	] as const;
	const focusDetailTab = (index: number) => {
		const tab = detailTabs[index];
		if (!tab) {
			return;
		}
		setActiveTab(tab.id);
		detailTabRefs.current[index]?.focus();
	};
	const handleDetailTabKeyDown = (
		event: KeyboardEvent<HTMLButtonElement>,
		index: number,
	) => {
		if (event.key === "ArrowRight") {
			event.preventDefault();
			focusDetailTab((index + 1) % detailTabs.length);
		} else if (event.key === "ArrowLeft") {
			event.preventDefault();
			focusDetailTab((index - 1 + detailTabs.length) % detailTabs.length);
		} else if (event.key === "Home") {
			event.preventDefault();
			focusDetailTab(0);
		} else if (event.key === "End") {
			event.preventDefault();
			focusDetailTab(detailTabs.length - 1);
		}
	};

	return (
		<div
			className="box-score-detail-stack"
			data-box-score-detail-active-tab={activeTab}
			data-box-score-detail-tabs=""
		>
			<div className="box-score-detail-main">
				<section className="box-score-detail-section">
					<div
						aria-label={i18n.t("boxScoreWrapper.gameDetailTabs", {
							ns: "ui",
							defaultValue: "Game detail tabs",
						})}
						className="box-score-detail-tabs"
						role="tablist"
					>
						{detailTabs.map((tab) => (
							<button
								aria-controls={`${detailTabsId}-box-score-detail-panel-${tab.id}`}
								aria-selected={activeTab === tab.id}
								className={clsx("box-score-detail-tab", {
									"box-score-detail-tab-active": activeTab === tab.id,
								})}
								data-box-score-detail-tab={tab.id}
								id={`${detailTabsId}-box-score-detail-tab-${tab.id}`}
								key={tab.id}
								onKeyDown={(event) => {
									handleDetailTabKeyDown(
										event,
										detailTabs.findIndex((detailTab) => detailTab.id === tab.id),
									);
								}}
								onClick={() => {
									setActiveTab(tab.id);
								}}
								ref={(element) => {
									detailTabRefs.current[
										detailTabs.findIndex((detailTab) => detailTab.id === tab.id)
									] = element;
								}}
								role="tab"
								tabIndex={activeTab === tab.id ? 0 : -1}
								type="button"
							>
								{tab.label}
							</button>
						))}
					</div>
					<div className="box-score-table-wrap">
						<div
							aria-labelledby={`${detailTabsId}-box-score-detail-tab-lineScore`}
							data-box-score-detail-panel="lineScore"
							hidden={activeTab !== "lineScore"}
							id={`${detailTabsId}-box-score-detail-panel-lineScore`}
							role="tabpanel"
						>
							<table
								className="table table-sm mb-0 box-score-line-score-table"
								data-box-score-line-score-table=""
							>
								<thead>
									<tr>
										<th />
										{qtrs.map((info) => (
											<th
												key={info.label}
												className={
													info.bold ? undefined : "text-body-secondary"
												}
											>
												<MobileTooltip title={info.title}>
													<span>{info.label}</span>
												</MobileTooltip>
											</th>
										))}
									</tr>
								</thead>
								<tbody>
									{boxScore.teams.map((t: any) => (
										<tr key={t.abbrev}>
											<th>
												{t.tid >= 0 ? (
													<a
														href={helpers.leagueUrl([
															"roster",
															`${t.abbrev}_${t.tid}`,
															boxScore.season,
														])}
													>
														{translateTeamShortName(t, t.abbrev)}
													</a>
												) : (
													t.abbrev
												)}
											</th>
											{t.ptsQtrs.map((pts: number, i: number) => (
												<td key={i}>{pts}</td>
											))}
											{range(numPeriods - t.ptsQtrs.length).map((i) => (
												<td key={i}>-</td>
											))}
											<th>{t.pts}</th>
											{shootout ? <th>{t.sPts}</th> : null}
										</tr>
									))}
								</tbody>
							</table>
						</div>
						<div
							aria-labelledby={`${detailTabsId}-box-score-detail-tab-fourFactors`}
							data-box-score-detail-panel="fourFactors"
							hidden={activeTab !== "fourFactors"}
							id={`${detailTabsId}-box-score-detail-panel-fourFactors`}
							role="tabpanel"
						>
							<FourFactors teams={boxScore.teams} />
						</div>
					</div>
				</section>
			</div>
			{showNextPrev ? (
				<div className="box-score-game-nav">
					<a
						className={clsx("btn", "btn-light-bordered", "box-score-game-nav-btn", {
							disabled: prevGid === undefined,
						})}
						href={helpers.leagueUrl([
							"game_log",
							abbrev === "special" ? abbrev : `${abbrev}_${tid}`,
							boxScore.season,
							prevGid,
						])}
					>
						{i18n.t("boxScoreWrapper.previousGame", {
							ns: "ui",
							defaultValue: "Previous game",
						})}
					</a>
					<NextButton
						abbrev={abbrev}
						boxScore={boxScore}
						currentGidInList={currentGidInList}
						nextGid={nextGid}
						tid={tid}
					/>
				</div>
			) : null}
		</div>
	);
};

const PlayoffRecord = ({
	numGamesToWinSeries,
	season,
	t0,
	t1,
}: {
	numGamesToWinSeries: number;
	season: number;
	t0: {
		abbrev: string;
		playoffs?: { won: number; lost: number };
		tid: number;
	};
	t1: {
		abbrev: string;
		playoffs?: { won: number; lost: number };
		tid: number;
	};
}) => {
	if (
		numGamesToWinSeries === undefined ||
		numGamesToWinSeries <= 1 ||
		!t0.playoffs ||
		!t1.playoffs
	) {
		return null;
	}

	type RequiredPlayoffsTeam = Required<typeof t0>;
	let winning: RequiredPlayoffsTeam;
	let losing: RequiredPlayoffsTeam;
	if (t0.playoffs.won > t1.playoffs.won) {
		winning = t0 as RequiredPlayoffsTeam;
		losing = t1 as RequiredPlayoffsTeam;
	} else {
		winning = t1 as RequiredPlayoffsTeam;
		losing = t0 as RequiredPlayoffsTeam;
	}

	const record = (
		<a href={helpers.leagueUrl(["playoffs", season])}>
			{winning.playoffs.won}-{losing.playoffs.won}
		</a>
	);

	if (winning.playoffs.won === losing.playoffs.won) {
		return (
			<>
				{i18n.t("boxScoreWrapper.seriesTied", { ns: "ui", defaultValue: "Series tied" })} {record}
			</>
		);
	}

	const winningAbbrevLink = (
		<a
			href={helpers.leagueUrl([
				"roster",
				`${winning.abbrev}_${winning.tid}`,
				season,
			])}
		>
			<TeamAbbrevText tid={winning.tid} abbrev={winning.abbrev} />
		</a>
	);

	if (winning.playoffs.won === numGamesToWinSeries) {
		return (
			<>
				{winningAbbrevLink} {i18n.t("boxScoreWrapper.wonSeries", { ns: "ui", defaultValue: "won series" })} {record}
			</>
		);
	}

	return (
		<>
			{winningAbbrevLink} {i18n.t("boxScoreWrapper.leadsSeries", { ns: "ui", defaultValue: "leads series" })} {record}
		</>
	);
};

export const BoxScoreWrapper = ({
	abbrev,
	boxScore,
	currentGidInList,
	live,
	nextGid,
	playerTableDefaultOpen = true,
	playIndex,
	prevGid,
	showNextPrev,
	sportState,
	tid,
	Row,
}: {
	abbrev?: string;
	boxScore: any;
	currentGidInList?: boolean;
	live?: boolean;
	nextGid?: number;
	playerTableDefaultOpen?: boolean;
	playIndex?: number;
	prevGid?: number;
	showNextPrev?: boolean;
	sportState: any;
	tid?: number;
	Row: any;
}) => {
	const playerTableBodyId = `${useId().replaceAll(":", "")}-box-score-player-table-body`;
	const prevPlayIndex = useRef(playIndex);
	useEffect(() => {
		prevPlayIndex.current = playIndex;
	}, [playIndex]);
	// If more than one play has happend between renders, force update of every row of the live box score, in case a player was subbed out in the missing play
	let forceRowUpdate =
		playIndex !== undefined &&
		prevPlayIndex.current !== undefined &&
		playIndex - prevPlayIndex.current > 1;

	// Always update when game ends (needed to show DNPs after live sim ends)
	if (boxScore.gameOver) {
		forceRowUpdate = true;
	}

	// Historical games will have boxScore.won.name and boxScore.lost.name so use that for ordering, but live games
	// won't. This is hacky, because the existence of this property is just a historical coincidence, and maybe it'll
	// change in the future.
	const t0 =
		boxScore.won?.name !== undefined ? boxScore.won : boxScore.teams[0];
	const t1 =
		boxScore.lost?.name !== undefined ? boxScore.lost : boxScore.teams[1];

	let forcedWinText = null;
	if (boxScore.forceWin !== undefined) {
		const pure = boxScore.forceWin <= 500;

		// Live game sim still has final score in won/lost.pts
		const tie = getWinner([boxScore.won, boxScore.lost]) === -1;

		forcedWinText = (
			<>
				<br />
				Forced {tie ? "tie" : "win"} in{" "}
				<span
					className={pure ? "text-success" : "text-danger"}
				>
					<MobileTooltip
						title={
							pure
								? `${tie ? "Tie" : "Win"} was forced without giving a bonus to ${
										tie ? "either" : "the winning"
									} team`
								: `Forcing the ${tie ? "tie" : "win"} required giving ${
										tie ? "a" : "the winning"
									} team a bonus`
						}
					>
						<span>{helpers.numberWithCommas(boxScore.forceWin)}</span>
					</MobileTooltip>
				</span>{" "}
				{boxScore.forceWin === 1 ? "try" : "tries"}.
			</>
		);
	}

	const playerTable = (
		<>
			<BoxScore
				boxScore={boxScore}
				Row={Row}
				forceRowUpdate={forceRowUpdate}
				sportState={sportState}
			/>
			<div
				className="box-score-game-meta text-body-secondary small"
				data-box-score-game-meta=""
			>
				{boxScore.att !== undefined ? (
					<div>
						{i18n.t("boxScoreWrapper.attendance", {
							ns: "ui",
							defaultValue: "Attendance: {{attendance}}",
							attendance: helpers.numberWithCommas(boxScore.att),
						})}
					</div>
				) : null}
				{boxScore.neutralSite ? (
					<div>
						{i18n.t("boxScoreWrapper.neutralSite", {
							ns: "ui",
							defaultValue: "Played at a neutral site",
						})}
					</div>
				) : null}
				{forcedWinText ? <div>{forcedWinText}</div> : null}
			</div>
		</>
	);

	return (
		<>
			<div className="d-flex align-items-center">
				{live ? <TeamLogo season={boxScore.season} t={t0} /> : null}
				<div className="box-score-header-center mx-auto mb-2 mw-100">
					{!live ? <HeadlineScore boxScore={boxScore} /> : null}
					<DetailedScore
						abbrev={abbrev}
						boxScore={boxScore}
						currentGidInList={currentGidInList}
						key={boxScore.gid}
						nextGid={nextGid}
						prevGid={prevGid}
						showNextPrev={showNextPrev}
						tid={tid}
					/>
					<div className="mt-sm-1 text-center">
						<PlayoffRecord
							numGamesToWinSeries={boxScore.numGamesToWinSeries}
							season={boxScore.season}
							t0={t0}
							t1={t1}
						/>
					</div>
				</div>
				{live ? <TeamLogo season={boxScore.season} t={t1} /> : null}
			</div>
			{playerTableDefaultOpen ? (
				<section
					className="box-score-player-table-depth"
					data-mobile-box-score-player-depth=""
					data-mobile-box-score-player-depth-default="open"
				>
					{playerTable}
				</section>
			) : (
				<details
					className="box-score-player-table-depth mt-3"
					data-mobile-box-score-player-depth=""
					data-mobile-box-score-player-depth-default="closed"
					data-native-details-host="box-score-player-depth"
				>
					<summary
						aria-controls={playerTableBodyId}
						className="box-score-player-table-summary"
						data-mobile-box-score-player-depth-summary=""
					>
						<span>
							{i18n.t("boxScoreWrapper.fullPlayerStats", {
								ns: "ui",
								defaultValue: "Complete player box score",
							})}
						</span>
					</summary>
					<div
						className="box-score-player-table-body"
						data-mobile-box-score-player-depth-body=""
						id={playerTableBodyId}
					>
						{playerTable}
					</div>
				</details>
			)}
		</>
	);
};
